export class PingPongService {
    log;
    crypto;
    client = null;
    running = false;
    sleepTimer = null;
    sleepResolver = null;
    pings = new Map();
    lastPingAt = null;
    lastPongAt = null;
    lastTimeoutAt = null;
    constructor(options) {
        this.log = options?.logger ?? {};
        const cryptoAny = globalThis.crypto;
        this.crypto = options?.crypto === undefined ? (isCrypto(cryptoAny) ? cryptoAny : null) : options.crypto;
    }
    configure(client) {
        this.client = client;
    }
    start() {
        if (this.running)
            return;
        this.running = true;
        this.reset();
        void this.loop();
    }
    stop() {
        this.running = false;
        this.clearSleep();
        this.reset();
    }
    async ping() {
        const client = this.client;
        if (!client)
            return;
        if (client.state !== "open")
            return;
        const nonce = this.randomNonce();
        await client.sendPing(nonce);
        const now = Date.now();
        this.lastPingAt = now;
        this.pings.set(nonce, now);
    }
    pong(nonce) {
        const pingDate = this.pings.get(nonce);
        if (!pingDate)
            return;
        this.pings.delete(nonce);
        this.lastPongAt = Date.now();
    }
    async loop() {
        while (this.running) {
            await this.checkConnection();
            await this.sleep(10_000);
            if (!this.running)
                break;
            await this.ping();
        }
    }
    reset() {
        this.pings.clear();
    }
    async checkConnection() {
        const client = this.client;
        if (!client)
            return;
        if (client.state !== "open")
            return;
        const now = Date.now();
        const oldestPendingPingAt = [...this.pings.values()].reduce((oldest, timestamp) => (oldest == null || timestamp < oldest ? timestamp : oldest), null);
        if (oldestPendingPingAt == null)
            return;
        const oldestPendingAgeMs = now - oldestPendingPingAt;
        if (oldestPendingAgeMs <= 30_000)
            return;
        this.lastTimeoutAt = now;
        this.log.warn?.(`Ping timeout, reconnecting (pending=${this.pings.size}, oldestPendingAgeMs=${oldestPendingAgeMs}${this.lastPongAt != null ? `, lastPongAgeMs=${now - this.lastPongAt}` : ""})`);
        this.reset();
        await client.reconnect({ cause: "ping-timeout" });
    }
    async sleep(ms) {
        if (ms <= 0)
            return;
        await new Promise((resolve) => {
            this.sleepResolver = resolve;
            this.sleepTimer = setTimeout(() => {
                this.sleepTimer = null;
                this.sleepResolver = null;
                resolve();
            }, ms);
        });
    }
    clearSleep() {
        if (this.sleepTimer) {
            clearTimeout(this.sleepTimer);
            this.sleepTimer = null;
        }
        if (this.sleepResolver) {
            this.sleepResolver();
            this.sleepResolver = null;
        }
    }
    randomNonce() {
        if (this.crypto) {
            const buf = new Uint32Array(2);
            this.crypto.getRandomValues(buf);
            // Under `noUncheckedIndexedAccess`, `buf[0]` becomes `number | undefined`.
            // This array is fixed-size (2), so the cast is safe.
            const hi = buf[0];
            const lo = buf[1];
            return (BigInt(hi) << 32n) | BigInt(lo);
        }
        return BigInt(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER));
    }
    getDiagnostics() {
        const now = Date.now();
        const oldestPendingPingAt = [...this.pings.values()].reduce((oldest, timestamp) => (oldest == null || timestamp < oldest ? timestamp : oldest), null);
        return {
            running: this.running,
            pendingCount: this.pings.size,
            lastPingAt: this.lastPingAt,
            lastPongAt: this.lastPongAt,
            lastTimeoutAt: this.lastTimeoutAt,
            oldestPendingPingAt,
            oldestPendingPingAgeMs: oldestPendingPingAt != null ? now - oldestPendingPingAt : null,
        };
    }
}
const isCrypto = (value) => typeof value === "object" && value !== null && "getRandomValues" in value && typeof value.getRandomValues === "function";
